/**
 * CloudWatch RUM Event Tracking Utilities
 *
 * This module provides helper functions to track user interactions throughout the app.
 * Events cover navigation, CRUD operations, button clicks, and user engagement.
 *
 * IMPORTANT: All RUM tracking is non-blocking and errors are silently logged
 * to prevent disruption of the user experience.
 *
 * SECURITY: All tracking data is sanitized to remove PHI before sending to RUM.
 */

import { recordRumError, recordRumEvent } from '@/providers/RumProvider';
import { sanitizeTrackingData } from './phi-sanitization';

// ============================================================================
// EVENT TYPE ENUMS
// ============================================================================

/**
 * Navigation and Page View Events
 */
export enum NavigationEventType {
  PAGE_VIEW = 'navigation.page_view',
  TAB_CHANGE = 'navigation.tab_change',
  MODAL_OPEN = 'navigation.modal_open',
  MODAL_CLOSE = 'navigation.modal_close',
  DRAWER_OPEN = 'navigation.drawer_open',
  DRAWER_CLOSE = 'navigation.drawer_close',
  SIDEBAR_TOGGLE = 'navigation.sidebar_toggle',
}

/**
 * Patient-related Events
 */
export enum PatientEventType {
  // View Events
  PATIENT_LIST_VIEW = 'patient.list_view',
  PATIENT_DETAILS_VIEW = 'patient.details_view',
  PATIENT_CHART_VIEW = 'patient.chart_view',
  PATIENT_SEARCH = 'patient.search',
  PATIENT_SEARCH_RESULT_CLICK = 'patient.search_result_click',

  // Create/Edit Events
  PATIENT_CREATE_STARTED = 'patient.create_started',
  PATIENT_CREATE_SUCCESS = 'patient.create_success',
  PATIENT_CREATE_FAILED = 'patient.create_failed',
  PATIENT_UPDATE_STARTED = 'patient.update_started',
  PATIENT_UPDATE_SUCCESS = 'patient.update_success',
  PATIENT_UPDATE_FAILED = 'patient.update_failed',

  // Chart Events
  CHART_CREATE_STARTED = 'patient.chart.create_started',
  CHART_CREATE_SUCCESS = 'patient.chart.create_success',
  CHART_CREATE_FAILED = 'patient.chart.create_failed',
  CHART_SWITCH = 'patient.chart.switch',
}

/**
 * Task-related Events
 */
export enum TaskEventType {
  // View Events
  TASK_LIST_VIEW = 'task.list_view',
  TASK_DETAILS_VIEW = 'task.details_view',
  TASK_KANBAN_VIEW = 'task.kanban_view',

  // Create/Edit Events
  TASK_CREATE_STARTED = 'task.create_started',
  TASK_CREATE_SUCCESS = 'task.create_success',
  TASK_CREATE_FAILED = 'task.create_failed',
  TASK_UPDATE_STARTED = 'task.update_started',
  TASK_UPDATE_SUCCESS = 'task.update_success',
  TASK_UPDATE_FAILED = 'task.update_failed',
  TASK_ASSIGN = 'task.assign',
  TASK_STATUS_CHANGE = 'task.status_change',
  TASK_COMPLETE = 'task.complete',
}

/**
 * Document-related Events
 */
export enum DocumentEventType {
  DOCUMENT_LIST_VIEW = 'document.list_view',
  DOCUMENT_VIEW = 'document.view',
  DOCUMENT_DOWNLOAD = 'document.download',
  DOCUMENT_UPLOAD_STARTED = 'document.upload_started',
  DOCUMENT_UPLOAD_SUCCESS = 'document.upload_success',
  DOCUMENT_UPLOAD_FAILED = 'document.upload_failed',
  DOCUMENT_DELETE = 'document.delete',
}

/**
 * Clinical Notes Events
 */
export enum NotesEventType {
  NOTE_LIST_VIEW = 'note.list_view',
  NOTE_CREATE_STARTED = 'note.create_started',
  NOTE_CREATE_SUCCESS = 'note.create_success',
  NOTE_CREATE_FAILED = 'note.create_failed',
  NOTE_EDIT_STARTED = 'note.edit_started',
  NOTE_EDIT_SUCCESS = 'note.edit_success',
  NOTE_EDIT_FAILED = 'note.edit_failed',
  NOTE_REPLY_STARTED = 'note.reply_started',
  NOTE_REPLY_SUCCESS = 'note.reply_success',
  NOTE_REPLY_FAILED = 'note.reply_failed',
  NOTE_ARCHIVE = 'note.archive',
  NOTE_ATTACHMENT_VIEW = 'note.attachment_view',
  // Per-keystroke editor responsiveness sample. Emitted from the
  // keystroke-lag tracker (utils/keystroke-lag-tracker.ts) when a typing
  // interaction in the Notes editor exceeds a noticeable threshold. Carries
  // session-level chartSwitch correlation so we can pivot RUM to confirm
  // the "lag worsens after N chart switches" failure mode observed in
  // production INP data (see tmp/test-evidence/rum-typing-lag-7day-2026-04-23.md).
  NOTE_KEYSTROKE_LAG = 'note.keystroke_lag',
}

/**
 * VOB (Verification of Benefits) Events
 */
export enum VobEventType {
  VOB_VIEW = 'vob.view',
  VOB_CREATE_STARTED = 'vob.create_started',
  VOB_CREATE_SUCCESS = 'vob.create_success',
  VOB_CREATE_FAILED = 'vob.create_failed',
  VOB_UPDATE_STARTED = 'vob.update_started',
  VOB_UPDATE_SUCCESS = 'vob.update_success',
  VOB_UPDATE_FAILED = 'vob.update_failed',
  VOB_REQUEST = 'vob.request',
}

/**
 * HST (Home Sleep Test) Events
 */
export enum HstEventType {
  HST_VIEW = 'hst.view',
  HST_ORDER_STARTED = 'hst.order_started',
  HST_ORDER_SUCCESS = 'hst.order_success',
  HST_ORDER_FAILED = 'hst.order_failed',
  HST_LINK_GENERATE = 'hst.link_generate',
  HST_STATUS_UPDATE = 'hst.status_update',
}

/**
 * Consult Events
 */
export enum ConsultEventType {
  CONSULT_VIEW = 'consult.view',
  CONSULT_SCHEDULE_STARTED = 'consult.schedule_started',
  CONSULT_SCHEDULE_SUCCESS = 'consult.schedule_success',
  CONSULT_SCHEDULE_FAILED = 'consult.schedule_failed',
  CONSULT_STATUS_UPDATE = 'consult.status_update',
}

/**
 * Preauth Events
 */
export enum PreauthEventType {
  PREAUTH_VIEW = 'preauth.view',
  PREAUTH_CREATE_STARTED = 'preauth.create_started',
  PREAUTH_CREATE_SUCCESS = 'preauth.create_success',
  PREAUTH_CREATE_FAILED = 'preauth.create_failed',
  PREAUTH_STATUS_UPDATE = 'preauth.status_update',
}

/**
 * Assessment (Chart Assessments Tab) Events
 */
export enum AssessmentEventType {
  ASSESSMENT_VIEW = 'assessment.view',
  ASSESSMENT_LIST_LOADED = 'assessment.list_loaded',
  ASSESSMENT_DETAIL_LOADED = 'assessment.detail_loaded',
  ASSESSMENT_DETAIL_FAILED = 'assessment.detail_failed',
  ASSESSMENT_SELECTION_CHANGE = 'assessment.selection_change',
  ASSESSMENT_EMPTY_STATE = 'assessment.empty_state',
}

/**
 * Prezo (Payment Plan) Events
 */
export enum PrezoEventType {
  PREZO_VIEW = 'prezo.view',
  PREZO_CREATE_STARTED = 'prezo.create_started',
  PREZO_CREATE_SUCCESS = 'prezo.create_success',
  PREZO_CREATE_FAILED = 'prezo.create_failed',
  PREZO_STATUS_UPDATE = 'prezo.status_update',
}

/**
 * Partner Referral API Events
 */
export enum PartnerReferralEventType {
  // API Docs Page
  API_DOCS_PAGE_VIEW = 'partner.api_docs.page_view',
  API_DOCS_CREDENTIALS_LOADED = 'partner.api_docs.credentials_loaded',
  API_DOCS_CREDENTIALS_FAILED = 'partner.api_docs.credentials_failed',
  API_DOCS_CODE_COPY = 'partner.api_docs.code_copy',
  API_DOCS_TAB_CHANGE = 'partner.api_docs.tab_change',
  API_DOCS_SECTION_TOGGLE = 'partner.api_docs.section_toggle',
  API_DOCS_ACCESS_DENIED = 'partner.api_docs.access_denied',

  // Referrals Report Page
  REFERRALS_LIST_VIEW = 'partner.referrals.list_view',
  REFERRALS_LIST_LOADED = 'partner.referrals.list_loaded',
  REFERRALS_LIST_FAILED = 'partner.referrals.list_failed',
  REFERRALS_DETAIL_VIEW = 'partner.referrals.detail_view',
  REFERRALS_DETAIL_LOADED = 'partner.referrals.detail_loaded',
  REFERRALS_DETAIL_FAILED = 'partner.referrals.detail_failed',
  REFERRALS_SEARCH = 'partner.referrals.search',
  REFERRALS_FILTER_CHANGE = 'partner.referrals.filter_change',
  REFERRALS_SORT_CHANGE = 'partner.referrals.sort_change',
  REFERRALS_PAGE_CHANGE = 'partner.referrals.page_change',
  REFERRALS_ACCESS_DENIED = 'partner.referrals.access_denied',
}

/**
 * Button/Action Events
 */
export enum ActionEventType {
  BUTTON_CLICK = 'action.button_click',
  LINK_CLICK = 'action.link_click',
  MENU_ITEM_CLICK = 'action.menu_item_click',
  DROPDOWN_SELECT = 'action.dropdown_select',
  FILTER_APPLY = 'action.filter_apply',
  FILTER_CLEAR = 'action.filter_clear',
  SORT_CHANGE = 'action.sort_change',
  PAGINATION = 'action.pagination',
  COPY_TO_CLIPBOARD = 'action.copy_to_clipboard',
  EXPORT = 'action.export',
  REFRESH = 'action.refresh',
}

/**
 * Authentication Events
 */
export enum AuthEventType {
  LOGIN_STARTED = 'auth.login_started',
  LOGIN_SUCCESS = 'auth.login_success',
  LOGIN_FAILED = 'auth.login_failed',
  LOGOUT = 'auth.logout',
  SESSION_REFRESH = 'auth.session_refresh',
  ROLE_SWITCH = 'auth.role_switch',
}

/**
 * Notification Events
 */
export enum NotificationEventType {
  NOTIFICATION_VIEW = 'notification.view',
  NOTIFICATION_CLICK = 'notification.click',
  NOTIFICATION_DISMISS = 'notification.dismiss',
  NOTIFICATION_SETTINGS_CHANGE = 'notification.settings_change',
}

// ============================================================================
// BASE INTERFACES
// ============================================================================

/**
 * Base metadata for all events
 */
interface BaseEventMetadata {
  tenantId?: string;
  practiceId?: string;
  userId?: string;
  userRole?: string;
}

/**
 * Patient context for patient-related events
 */
interface PatientContext {
  saxId?: string;
  chartId?: string;
  firstName?: string;
  lastName?: string;
}

// ============================================================================
// TRACKING FUNCTIONS - NAVIGATION
// ============================================================================

export async function trackPageView(
  pageName: string,
  metadata: BaseEventMetadata & { path?: string; referrer?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      pageName,
      path:
        metadata.path ||
        (typeof window !== 'undefined' ? window.location.pathname : ''),
      practiceId: metadata.practiceId,
      referrer: metadata.referrer,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📄 ${NavigationEventType.PAGE_VIEW}`, sanitized);
    recordRumEvent(NavigationEventType.PAGE_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track page view:', error);
  }
}

export function trackTabChange(
  tabName: string,
  metadata: BaseEventMetadata & { previousTab?: string; context?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      practiceId: metadata.practiceId,
      previousTab: metadata.previousTab,
      tabName,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🗂️ ${NavigationEventType.TAB_CHANGE}`, sanitized);
    recordRumEvent(NavigationEventType.TAB_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track tab change:', error);
  }
}

export function trackModalOpen(
  modalName: string,
  metadata: BaseEventMetadata & { triggeredBy?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      modalName,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      triggeredBy: metadata.triggeredBy,
    });
    console.log(`[RUM] 🪟 ${NavigationEventType.MODAL_OPEN}`, sanitized);
    recordRumEvent(NavigationEventType.MODAL_OPEN, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track modal open:', error);
  }
}

export function trackModalClose(
  modalName: string,
  metadata: BaseEventMetadata & { action?: 'save' | 'cancel' | 'dismiss' } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      action: metadata.action,
      modalName,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🚪 ${NavigationEventType.MODAL_CLOSE}`, sanitized);
    recordRumEvent(NavigationEventType.MODAL_CLOSE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track modal close:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - PATIENT
// ============================================================================

export async function trackPatientListView(
  metadata: BaseEventMetadata & {
    resultCount?: number;
    filterApplied?: boolean;
  } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      filterApplied: metadata.filterApplied,
      practiceId: metadata.practiceId,
      resultCount: metadata.resultCount,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 👥 ${PatientEventType.PATIENT_LIST_VIEW}`, sanitized);
    recordRumEvent(PatientEventType.PATIENT_LIST_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient list view:', error);
  }
}

export async function trackPatientDetailsView(
  metadata: BaseEventMetadata & PatientContext & { source?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      firstName: metadata.firstName,
      lastName: metadata.lastName,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      source: metadata.source,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 👤 ${PatientEventType.PATIENT_DETAILS_VIEW}`, sanitized);
    recordRumEvent(PatientEventType.PATIENT_DETAILS_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient details view:', error);
  }
}

export async function trackPatientSearch(
  metadata: BaseEventMetadata & {
    searchQuery?: string;
    resultCount?: number;
  } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      // Don't include actual search query as it may contain PHI
      hasSearchQuery: !!metadata.searchQuery,
      practiceId: metadata.practiceId,
      resultCount: metadata.resultCount,
      searchQueryLength: metadata.searchQuery?.length || 0,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔍 ${PatientEventType.PATIENT_SEARCH}`, sanitized);
    recordRumEvent(PatientEventType.PATIENT_SEARCH, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient search:', error);
  }
}

export async function trackPatientSearchResultClick(
  metadata: BaseEventMetadata &
    PatientContext & { resultPosition?: number } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      firstName: metadata.firstName,
      lastName: metadata.lastName,
      practiceId: metadata.practiceId,
      resultPosition: metadata.resultPosition,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 🖱️ ${PatientEventType.PATIENT_SEARCH_RESULT_CLICK}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_SEARCH_RESULT_CLICK, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient search result click:', error);
  }
}

export async function trackPatientCreateStarted(
  metadata: BaseEventMetadata = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ➕ ${PatientEventType.PATIENT_CREATE_STARTED}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient create started:', error);
  }
}

export async function trackPatientCreateSuccess(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      firstName: metadata.firstName,
      lastName: metadata.lastName,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${PatientEventType.PATIENT_CREATE_SUCCESS}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient create success:', error);
  }
}

export async function trackPatientCreateFailed(
  metadata: BaseEventMetadata,
  error: Error | string,
) {
  try {
    const sanitized = await sanitizeTrackingData({
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${PatientEventType.PATIENT_CREATE_FAILED}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'patient_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track patient create failed:', err);
  }
}

export async function trackPatientUpdateStarted(
  metadata: BaseEventMetadata & PatientContext & { fields?: string[] } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      fieldsUpdated: metadata.fields?.length || 0,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✏️ ${PatientEventType.PATIENT_UPDATE_STARTED}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_UPDATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient update started:', error);
  }
}

export async function trackPatientUpdateSuccess(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      firstName: metadata.firstName,
      lastName: metadata.lastName,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${PatientEventType.PATIENT_UPDATE_SUCCESS}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_UPDATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track patient update success:', error);
  }
}

export async function trackPatientUpdateFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${PatientEventType.PATIENT_UPDATE_FAILED}`,
      sanitized,
    );
    recordRumEvent(PatientEventType.PATIENT_UPDATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, {
        context: 'patient_update',
        saxId: metadata.saxId,
      });
    }
  } catch (err) {
    console.error('[RUM] Failed to track patient update failed:', err);
  }
}

export async function trackChartSwitch(
  metadata: BaseEventMetadata &
    PatientContext & { previousChartId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      previousChartId: metadata.previousChartId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔄 ${PatientEventType.CHART_SWITCH}`, sanitized);
    recordRumEvent(PatientEventType.CHART_SWITCH, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track chart switch:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - TASK
// ============================================================================

export async function trackTaskListView(
  metadata: BaseEventMetadata & {
    taskCount?: number;
    viewType?: 'list' | 'kanban';
  } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      practiceId: metadata.practiceId,
      taskCount: metadata.taskCount,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      viewType: metadata.viewType || 'list',
    });
    console.log(`[RUM] 📋 ${TaskEventType.TASK_LIST_VIEW}`, sanitized);
    recordRumEvent(TaskEventType.TASK_LIST_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task list view:', error);
  }
}

export async function trackTaskDetailsView(
  metadata: BaseEventMetadata & { taskId?: string; taskType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      practiceId: metadata.practiceId,
      taskId: metadata.taskId,
      taskType: metadata.taskType,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📄 ${TaskEventType.TASK_DETAILS_VIEW}`, sanitized);
    recordRumEvent(TaskEventType.TASK_DETAILS_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task details view:', error);
  }
}

export async function trackTaskCreateStarted(
  metadata: BaseEventMetadata & PatientContext & { taskType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      taskType: metadata.taskType,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ➕ ${TaskEventType.TASK_CREATE_STARTED}`, sanitized);
    recordRumEvent(TaskEventType.TASK_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task create started:', error);
  }
}

export async function trackTaskCreateSuccess(
  metadata: BaseEventMetadata &
    PatientContext & { taskId?: string; taskType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      taskId: metadata.taskId,
      taskType: metadata.taskType,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${TaskEventType.TASK_CREATE_SUCCESS}`, sanitized);
    recordRumEvent(TaskEventType.TASK_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task create success:', error);
  }
}

export async function trackTaskCreateFailed(
  metadata: BaseEventMetadata & PatientContext & { taskType?: string },
  error: Error | string,
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      taskType: metadata.taskType,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${TaskEventType.TASK_CREATE_FAILED}`, sanitized);
    recordRumEvent(TaskEventType.TASK_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'task_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track task create failed:', err);
  }
}

export async function trackTaskStatusChange(
  metadata: BaseEventMetadata & {
    taskId?: string;
    oldStatus?: string;
    newStatus?: string;
  } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      newStatus: metadata.newStatus,
      oldStatus: metadata.oldStatus,
      practiceId: metadata.practiceId,
      taskId: metadata.taskId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔄 ${TaskEventType.TASK_STATUS_CHANGE}`, sanitized);
    recordRumEvent(TaskEventType.TASK_STATUS_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task status change:', error);
  }
}

export async function trackTaskAssign(
  metadata: BaseEventMetadata & { taskId?: string; assigneeId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      hasAssignee: !!metadata.assigneeId,
      practiceId: metadata.practiceId,
      taskId: metadata.taskId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 👤 ${TaskEventType.TASK_ASSIGN}`, sanitized);
    recordRumEvent(TaskEventType.TASK_ASSIGN, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track task assign:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - DOCUMENTS
// ============================================================================

export async function trackDocumentView(
  metadata: BaseEventMetadata &
    PatientContext & { documentId?: string; documentType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      documentId: metadata.documentId,
      documentType: metadata.documentType,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📄 ${DocumentEventType.DOCUMENT_VIEW}`, sanitized);
    recordRumEvent(DocumentEventType.DOCUMENT_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track document view:', error);
  }
}

export async function trackDocumentUploadStarted(
  metadata: BaseEventMetadata &
    PatientContext & { documentType?: string; fileSize?: number } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      documentType: metadata.documentType,
      fileSize: metadata.fileSize,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 📤 ${DocumentEventType.DOCUMENT_UPLOAD_STARTED}`,
      sanitized,
    );
    recordRumEvent(DocumentEventType.DOCUMENT_UPLOAD_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track document upload started:', error);
  }
}

export async function trackDocumentUploadSuccess(
  metadata: BaseEventMetadata &
    PatientContext & { documentId?: string; documentType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      documentId: metadata.documentId,
      documentType: metadata.documentType,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${DocumentEventType.DOCUMENT_UPLOAD_SUCCESS}`,
      sanitized,
    );
    recordRumEvent(DocumentEventType.DOCUMENT_UPLOAD_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track document upload success:', error);
  }
}

export async function trackDocumentUploadFailed(
  metadata: BaseEventMetadata & PatientContext & { documentType?: string },
  error: Error | string,
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      documentType: metadata.documentType,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${DocumentEventType.DOCUMENT_UPLOAD_FAILED}`,
      sanitized,
    );
    recordRumEvent(DocumentEventType.DOCUMENT_UPLOAD_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'document_upload' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track document upload failed:', err);
  }
}

export async function trackDocumentDownload(
  metadata: BaseEventMetadata &
    PatientContext & { documentId?: string; documentType?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      documentId: metadata.documentId,
      documentType: metadata.documentType,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📥 ${DocumentEventType.DOCUMENT_DOWNLOAD}`, sanitized);
    recordRumEvent(DocumentEventType.DOCUMENT_DOWNLOAD, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track document download:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - CLINICAL NOTES
// ============================================================================

export async function trackNoteCreateStarted(
  metadata: BaseEventMetadata &
    PatientContext & { visibility?: 'private' | 'practice' } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      visibility: metadata.visibility,
    });
    console.log(`[RUM] 📝 ${NotesEventType.NOTE_CREATE_STARTED}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track note create started:', error);
  }
}

export async function trackNoteCreateSuccess(
  metadata: BaseEventMetadata &
    PatientContext & { noteId?: string; hasAttachments?: boolean } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      hasAttachments: metadata.hasAttachments,
      noteId: metadata.noteId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${NotesEventType.NOTE_CREATE_SUCCESS}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track note create success:', error);
  }
}

export async function trackNoteCreateFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${NotesEventType.NOTE_CREATE_FAILED}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'note_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track note create failed:', err);
  }
}

export async function trackNoteReplyStarted(
  metadata: BaseEventMetadata & PatientContext & { parentNoteId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      parentNoteId: metadata.parentNoteId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 💬 ${NotesEventType.NOTE_REPLY_STARTED}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_REPLY_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track note reply started:', error);
  }
}

export async function trackNoteReplySuccess(
  metadata: BaseEventMetadata &
    PatientContext & { noteId?: string; parentNoteId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      noteId: metadata.noteId,
      parentNoteId: metadata.parentNoteId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${NotesEventType.NOTE_REPLY_SUCCESS}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_REPLY_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track note reply success:', error);
  }
}

export async function trackNoteArchive(
  metadata: BaseEventMetadata & PatientContext & { noteId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      chartId: metadata.chartId,
      noteId: metadata.noteId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🗑️ ${NotesEventType.NOTE_ARCHIVE}`, sanitized);
    recordRumEvent(NotesEventType.NOTE_ARCHIVE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track note archive:', error);
  }
}

/**
 * Emit a single keystroke-lag observation from the Notes editor.
 *
 * This event is intended to be fired by `utils/keystroke-lag-tracker.ts` from
 * a `PerformanceObserver({type: 'event'})` callback. It carries ONLY timings
 * and counts — no editor content, no patient identifiers beyond the
 * already-sanitised tenant/practice context. PHI must never enter this path.
 *
 * Why this exists:
 *   - The Web Vitals INP signal is too coarse to attribute lag to a specific
 *     editor mount; CloudWatch RUM only gives us p75/p95 buckets per page.
 *   - The previous "typing-lag" event was claimed but never wired up; see
 *     tmp/test-evidence/rum-typing-lag-7day-2026-04-23.md for the
 *     investigation that exposed the gap.
 *   - We need session-level correlation between `chartSwitchesInSession` and
 *     keystroke latency to confirm/refute the "lag accumulates per chart
 *     navigation" hypothesis without resorting to manual repro.
 *
 * The tracker throttles emissions and applies a noticeable-threshold filter
 * to keep RUM ingest costs bounded. See the tracker module for the policy.
 */
export function trackNoteKeystrokeLag(
  metadata: BaseEventMetadata & {
    /** Interaction-to-next-paint duration for the keystroke, in ms. */
    valueMs: number;
    /** Number of distinct chart switches observed in this session so far. */
    chartSwitchesInSession: number;
    /** ms since this editor instance first appeared in the DOM. */
    editorMountAgeMs: number;
    /** Number of `.tiptap-editor` instances currently mounted. */
    editorInstanceCount: number;
    /** Number of `[data-tippy-root]` popovers currently mounted. */
    tippyCount: number;
    /** `document.querySelectorAll('*').length` snapshot. */
    domNodeCount: number;
    /** `performance.memory.usedJSHeapSize / 1MiB`, when available. */
    heapMb: number | null;
    /** `window.location.pathname` at emission time. */
    pageId: string;
    /** Browser-reported interaction type (event.name) for the sample. */
    interactionType: string;
  },
) {
  try {
    // We deliberately bypass `sanitizeTrackingData()` here for two reasons:
    //   1. It is async (uses Web Crypto for hashing PHI fields). This event
    //      fires from a PerformanceObserver hot-path; async + await would add
    //      a microtask hop per sample without benefit.
    //   2. The payload built below is a closed allowlist of timings, counts,
    //      and the page pathname. There is no name/email/phone/PHI here, so
    //      the sanitiser would be a no-op anyway. New keys MUST keep this
    //      property — never add patient-content fields to this event.
    const payload = {
      chartSwitchesInSession: metadata.chartSwitchesInSession,
      domNodeCount: metadata.domNodeCount,
      editorInstanceCount: metadata.editorInstanceCount,
      editorMountAgeMs: metadata.editorMountAgeMs,
      heapMb: metadata.heapMb,
      interactionType: metadata.interactionType,
      pageId: metadata.pageId,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      tippyCount: metadata.tippyCount,
      valueMs: metadata.valueMs,
    };
    // No console.log on the hot path; this fires up to ~1/sec across many
    // tabs and would flood devtools. recordRumEvent itself debug-logs.
    recordRumEvent(NotesEventType.NOTE_KEYSTROKE_LAG, payload);
  } catch (error) {
    console.error('[RUM] Failed to track note keystroke lag:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - VOB/HST/CONSULT/PREAUTH
// ============================================================================

export function trackVobView(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📋 ${VobEventType.VOB_VIEW}`, sanitized);
    recordRumEvent(VobEventType.VOB_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track VOB view:', error);
  }
}

export function trackVobCreateStarted(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ➕ ${VobEventType.VOB_CREATE_STARTED}`, sanitized);
    recordRumEvent(VobEventType.VOB_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track VOB create started:', error);
  }
}

export function trackVobCreateSuccess(
  metadata: BaseEventMetadata & PatientContext & { vobId?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      vobId: metadata.vobId,
    });
    console.log(`[RUM] ✅ ${VobEventType.VOB_CREATE_SUCCESS}`, sanitized);
    recordRumEvent(VobEventType.VOB_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track VOB create success:', error);
  }
}

export function trackVobCreateFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${VobEventType.VOB_CREATE_FAILED}`, sanitized);
    recordRumEvent(VobEventType.VOB_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'vob_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track VOB create failed:', err);
  }
}

export function trackHstView(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🛏️ ${HstEventType.HST_VIEW}`, sanitized);
    recordRumEvent(HstEventType.HST_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track HST view:', error);
  }
}

export function trackHstOrderStarted(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ➕ ${HstEventType.HST_ORDER_STARTED}`, sanitized);
    recordRumEvent(HstEventType.HST_ORDER_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track HST order started:', error);
  }
}

export function trackHstOrderSuccess(
  metadata: BaseEventMetadata & PatientContext & { orderId?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      orderId: metadata.orderId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${HstEventType.HST_ORDER_SUCCESS}`, sanitized);
    recordRumEvent(HstEventType.HST_ORDER_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track HST order success:', error);
  }
}

export function trackHstOrderFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${HstEventType.HST_ORDER_FAILED}`, sanitized);
    recordRumEvent(HstEventType.HST_ORDER_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'hst_order' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track HST order failed:', err);
  }
}

export function trackConsultView(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🩺 ${ConsultEventType.CONSULT_VIEW}`, sanitized);
    recordRumEvent(ConsultEventType.CONSULT_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track consult view:', error);
  }
}

export function trackConsultScheduleStarted(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 📅 ${ConsultEventType.CONSULT_SCHEDULE_STARTED}`,
      sanitized,
    );
    recordRumEvent(ConsultEventType.CONSULT_SCHEDULE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track consult schedule started:', error);
  }
}

export function trackConsultScheduleSuccess(
  metadata: BaseEventMetadata &
    PatientContext & { consultId?: string; appointmentDate?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      appointmentDate: metadata.appointmentDate,
      chartId: metadata.chartId,
      consultId: metadata.consultId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${ConsultEventType.CONSULT_SCHEDULE_SUCCESS}`,
      sanitized,
    );
    recordRumEvent(ConsultEventType.CONSULT_SCHEDULE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track consult schedule success:', error);
  }
}

export function trackConsultScheduleFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${ConsultEventType.CONSULT_SCHEDULE_FAILED}`,
      sanitized,
    );
    recordRumEvent(ConsultEventType.CONSULT_SCHEDULE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'consult_schedule' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track consult schedule failed:', err);
  }
}

export function trackPreauthView(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📋 ${PreauthEventType.PREAUTH_VIEW}`, sanitized);
    recordRumEvent(PreauthEventType.PREAUTH_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track preauth view:', error);
  }
}

export function trackPreauthCreateStarted(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ➕ ${PreauthEventType.PREAUTH_CREATE_STARTED}`,
      sanitized,
    );
    recordRumEvent(PreauthEventType.PREAUTH_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track preauth create started:', error);
  }
}

export function trackPreauthCreateSuccess(
  metadata: BaseEventMetadata & PatientContext & { preauthId?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      preauthId: metadata.preauthId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${PreauthEventType.PREAUTH_CREATE_SUCCESS}`,
      sanitized,
    );
    recordRumEvent(PreauthEventType.PREAUTH_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track preauth create success:', error);
  }
}

export function trackPreauthCreateFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${PreauthEventType.PREAUTH_CREATE_FAILED}`,
      sanitized,
    );
    recordRumEvent(PreauthEventType.PREAUTH_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'preauth_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track preauth create failed:', err);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - PREZO (PAYMENT PLAN)
// ============================================================================

export function trackPrezoView(
  metadata: BaseEventMetadata & PatientContext & { prezoId?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      prezoId: metadata.prezoId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 👁️ ${PrezoEventType.PREZO_VIEW}`, sanitized);
    recordRumEvent(PrezoEventType.PREZO_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track prezo view:', error);
  }
}

export function trackPrezoCreateStarted(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ➕ ${PrezoEventType.PREZO_CREATE_STARTED}`, sanitized);
    recordRumEvent(PrezoEventType.PREZO_CREATE_STARTED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track prezo create started:', error);
  }
}

export function trackPrezoCreateSuccess(
  metadata: BaseEventMetadata & PatientContext & { prezoId?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      prezoId: metadata.prezoId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${PrezoEventType.PREZO_CREATE_SUCCESS}`, sanitized);
    recordRumEvent(PrezoEventType.PREZO_CREATE_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track prezo create success:', error);
  }
}

export function trackPrezoCreateFailed(
  metadata: BaseEventMetadata & PatientContext,
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${PrezoEventType.PREZO_CREATE_FAILED}`, sanitized);
    recordRumEvent(PrezoEventType.PREZO_CREATE_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'prezo_create' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track prezo create failed:', err);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - ACTIONS/BUTTONS
// ============================================================================

export function trackButtonClick(
  buttonName: string,
  metadata: BaseEventMetadata & { context?: string; action?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      action: metadata.action,
      buttonName,
      context: metadata.context,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🖱️ ${ActionEventType.BUTTON_CLICK}`, sanitized);
    recordRumEvent(ActionEventType.BUTTON_CLICK, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track button click:', error);
  }
}

export function trackFilterApply(
  metadata: BaseEventMetadata & {
    filterName?: string;
    filterValue?: string;
    context?: string;
  } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      filterName: metadata.filterName,
      // Don't include actual filter values as they may contain PHI
      hasFilterValue: !!metadata.filterValue,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔽 ${ActionEventType.FILTER_APPLY}`, sanitized);
    recordRumEvent(ActionEventType.FILTER_APPLY, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track filter apply:', error);
  }
}

export function trackFilterClear(
  metadata: BaseEventMetadata & { context?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔄 ${ActionEventType.FILTER_CLEAR}`, sanitized);
    recordRumEvent(ActionEventType.FILTER_CLEAR, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track filter clear:', error);
  }
}

export function trackSortChange(
  metadata: BaseEventMetadata & {
    column?: string;
    direction?: 'asc' | 'desc';
    context?: string;
  } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      column: metadata.column,
      context: metadata.context,
      direction: metadata.direction,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ⬆️ ${ActionEventType.SORT_CHANGE}`, sanitized);
    recordRumEvent(ActionEventType.SORT_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track sort change:', error);
  }
}

export function trackPagination(
  metadata: BaseEventMetadata & {
    page?: number;
    pageSize?: number;
    totalItems?: number;
    context?: string;
  } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      page: metadata.page,
      pageSize: metadata.pageSize,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      totalItems: metadata.totalItems,
    });
    console.log(`[RUM] 📄 ${ActionEventType.PAGINATION}`, sanitized);
    recordRumEvent(ActionEventType.PAGINATION, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track pagination:', error);
  }
}

export function trackCopyToClipboard(
  metadata: BaseEventMetadata & { contentType?: string; context?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      contentType: metadata.contentType,
      context: metadata.context,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📋 ${ActionEventType.COPY_TO_CLIPBOARD}`, sanitized);
    recordRumEvent(ActionEventType.COPY_TO_CLIPBOARD, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track copy to clipboard:', error);
  }
}

export function trackExport(
  metadata: BaseEventMetadata & {
    exportType?: string;
    format?: string;
    itemCount?: number;
    context?: string;
  } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      exportType: metadata.exportType,
      format: metadata.format,
      itemCount: metadata.itemCount,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📤 ${ActionEventType.EXPORT}`, sanitized);
    recordRumEvent(ActionEventType.EXPORT, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track export:', error);
  }
}

export function trackRefresh(
  metadata: BaseEventMetadata & { context?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      context: metadata.context,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔄 ${ActionEventType.REFRESH}`, sanitized);
    recordRumEvent(ActionEventType.REFRESH, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track refresh:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - AUTH
// ============================================================================

export function trackLoginSuccess(
  metadata: BaseEventMetadata & { method?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      method: metadata.method,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${AuthEventType.LOGIN_SUCCESS}`, sanitized);
    recordRumEvent(AuthEventType.LOGIN_SUCCESS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track login success:', error);
  }
}

export function trackLogout(metadata: BaseEventMetadata = {}) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🚪 ${AuthEventType.LOGOUT}`, sanitized);
    recordRumEvent(AuthEventType.LOGOUT, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track logout:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - NOTIFICATIONS
// ============================================================================

export function trackNotificationClick(
  metadata: BaseEventMetadata & {
    notificationId?: string;
    notificationType?: string;
  } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      notificationId: metadata.notificationId,
      notificationType: metadata.notificationType,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 🔔 ${NotificationEventType.NOTIFICATION_CLICK}`,
      sanitized,
    );
    recordRumEvent(NotificationEventType.NOTIFICATION_CLICK, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track notification click:', error);
  }
}

export async function trackNotificationDismiss(
  metadata: BaseEventMetadata & { notificationId?: string } = {},
) {
  try {
    const sanitized = await sanitizeTrackingData({
      notificationId: metadata.notificationId,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 🔕 ${NotificationEventType.NOTIFICATION_DISMISS}`,
      sanitized,
    );
    recordRumEvent(NotificationEventType.NOTIFICATION_DISMISS, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track notification dismiss:', error);
  }
}

// ============================================================================
// TRACKING FUNCTIONS - ASSESSMENTS (Chart Assessments Tab)
// ============================================================================

export function trackAssessmentView(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      chartId: metadata.chartId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📝 ${AssessmentEventType.ASSESSMENT_VIEW}`, sanitized);
    recordRumEvent(AssessmentEventType.ASSESSMENT_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track assessment view:', error);
  }
}

export function trackAssessmentListLoaded(
  metadata: BaseEventMetadata &
    PatientContext & { assessmentCount?: number } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      assessmentCount: metadata.assessmentCount,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 📋 ${AssessmentEventType.ASSESSMENT_LIST_LOADED}`,
      sanitized,
    );
    recordRumEvent(AssessmentEventType.ASSESSMENT_LIST_LOADED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track assessment list loaded:', error);
  }
}

export function trackAssessmentDetailLoaded(
  metadata: BaseEventMetadata &
    PatientContext & {
      assessmentId?: string;
      answerCount?: number;
      riskScore?: number;
    } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      answerCount: metadata.answerCount,
      assessmentId: metadata.assessmentId,
      practiceId: metadata.practiceId,
      riskScore: metadata.riskScore,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ✅ ${AssessmentEventType.ASSESSMENT_DETAIL_LOADED}`,
      sanitized,
    );
    recordRumEvent(AssessmentEventType.ASSESSMENT_DETAIL_LOADED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track assessment detail loaded:', error);
  }
}

export function trackAssessmentDetailFailed(
  metadata: BaseEventMetadata & PatientContext & { assessmentId?: string } = {},
  error?: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      assessmentId: metadata.assessmentId,
      errorMessage:
        error instanceof Error ? error.message : error || 'Unknown error',
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] ❌ ${AssessmentEventType.ASSESSMENT_DETAIL_FAILED}`,
      sanitized,
    );
    recordRumEvent(AssessmentEventType.ASSESSMENT_DETAIL_FAILED, sanitized);

    if (error instanceof Error) {
      recordRumError(error, { context: 'assessment_detail' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track assessment detail failed:', err);
  }
}

export function trackAssessmentSelectionChange(
  metadata: BaseEventMetadata &
    PatientContext & { assessmentId?: string; selectedIndex?: number } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      assessmentId: metadata.assessmentId,
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      selectedIndex: metadata.selectedIndex,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 🔄 ${AssessmentEventType.ASSESSMENT_SELECTION_CHANGE}`,
      sanitized,
    );
    recordRumEvent(
      AssessmentEventType.ASSESSMENT_SELECTION_CHANGE,
      sanitized,
    );
  } catch (error) {
    console.error('[RUM] Failed to track assessment selection change:', error);
  }
}

export function trackAssessmentEmptyState(
  metadata: BaseEventMetadata & PatientContext = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      saxId: metadata.saxId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(
      `[RUM] 📭 ${AssessmentEventType.ASSESSMENT_EMPTY_STATE}`,
      sanitized,
    );
    recordRumEvent(AssessmentEventType.ASSESSMENT_EMPTY_STATE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track assessment empty state:', error);
  }
}

// ============================================================================
// ALIAS FUNCTIONS FOR CONVENIENCE
// ============================================================================

/**
 * Alias for trackPatientSearch - tracks search operations
 * @param metadata - Search metadata including context and results
 */
export async function trackSearch(
  metadata: BaseEventMetadata & {
    searchContext?: string;
    searchQuery?: string;
    resultCount?: number;
    hasResults?: boolean;
  } = {},
) {
  return trackPatientSearch(metadata);
}

/**
 * Alias for trackPatientSearchResultClick - tracks patient selection from search results
 * @param metadata - Selection metadata including patient context and source
 */
export async function trackPatientSelected(
  metadata: BaseEventMetadata &
    PatientContext & {
      selectionSource?: string;
      resultPosition?: number;
    } = {},
) {
  return trackPatientSearchResultClick(metadata);
}

// ============================================================================
// TRACKING FUNCTIONS - PARTNER REFERRAL API
// ============================================================================

/**
 * Track API Docs page view
 */
export function trackApiDocsPageView(
  metadata: BaseEventMetadata & { hasCredentials?: boolean; providerName?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      hasCredentials: metadata.hasCredentials,
      practiceId: metadata.practiceId,
      providerName: metadata.providerName,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📖 ${PartnerReferralEventType.API_DOCS_PAGE_VIEW}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_PAGE_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track API docs page view:', error);
  }
}

/**
 * Track API credentials loaded successfully
 */
export function trackApiDocsCredentialsLoaded(
  metadata: BaseEventMetadata & { providerName?: string; providerType?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      providerName: metadata.providerName,
      providerType: metadata.providerType,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔑 ${PartnerReferralEventType.API_DOCS_CREDENTIALS_LOADED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_CREDENTIALS_LOADED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track credentials loaded:', error);
  }
}

/**
 * Track API credentials fetch failure
 */
export function trackApiDocsCredentialsFailed(
  metadata: BaseEventMetadata = {},
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${PartnerReferralEventType.API_DOCS_CREDENTIALS_FAILED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_CREDENTIALS_FAILED, sanitized);
    if (error instanceof Error) {
      recordRumError(error, { context: 'api_docs_credentials_fetch' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track credentials failed:', err);
  }
}

/**
 * Track code example copied to clipboard
 */
export function trackApiDocsCodeCopy(
  metadata: BaseEventMetadata & { language?: string; section?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      language: metadata.language,
      practiceId: metadata.practiceId,
      section: metadata.section,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📋 ${PartnerReferralEventType.API_DOCS_CODE_COPY}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_CODE_COPY, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track code copy:', error);
  }
}

/**
 * Track code example tab change
 */
export function trackApiDocsTabChange(
  metadata: BaseEventMetadata & { fromTab?: string; toTab?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      fromTab: metadata.fromTab,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
      toTab: metadata.toTab,
    });
    console.log(`[RUM] 🗂️ ${PartnerReferralEventType.API_DOCS_TAB_CHANGE}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_TAB_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track tab change:', error);
  }
}

/**
 * Track collapsible section toggle
 */
export function trackApiDocsSectionToggle(
  metadata: BaseEventMetadata & { sectionName?: string; isOpen?: boolean } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      isOpen: metadata.isOpen,
      practiceId: metadata.practiceId,
      sectionName: metadata.sectionName,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📂 ${PartnerReferralEventType.API_DOCS_SECTION_TOGGLE}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_SECTION_TOGGLE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track section toggle:', error);
  }
}

/**
 * Track API docs access denied
 */
export function trackApiDocsAccessDenied(metadata: BaseEventMetadata = {}) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🚫 ${PartnerReferralEventType.API_DOCS_ACCESS_DENIED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.API_DOCS_ACCESS_DENIED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track access denied:', error);
  }
}

/**
 * Track referrals report list view
 */
export function trackReferralsListView(
  metadata: BaseEventMetadata & { resultCount?: number; filterApplied?: boolean } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      filterApplied: metadata.filterApplied,
      practiceId: metadata.practiceId,
      resultCount: metadata.resultCount,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 📋 ${PartnerReferralEventType.REFERRALS_LIST_VIEW}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_LIST_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referrals list view:', error);
  }
}

/**
 * Track referrals list loaded
 */
export function trackReferralsListLoaded(
  metadata: BaseEventMetadata & { resultCount?: number; durationMs?: number } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      durationMs: metadata.durationMs,
      practiceId: metadata.practiceId,
      resultCount: metadata.resultCount,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ✅ ${PartnerReferralEventType.REFERRALS_LIST_LOADED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_LIST_LOADED, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referrals list loaded:', error);
  }
}

/**
 * Track referrals list load failure
 */
export function trackReferralsListFailed(
  metadata: BaseEventMetadata = {},
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${PartnerReferralEventType.REFERRALS_LIST_FAILED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_LIST_FAILED, sanitized);
    if (error instanceof Error) {
      recordRumError(error, { context: 'referrals_list_fetch' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track referrals list failed:', err);
  }
}

/**
 * Track referral detail view (drawer opened)
 */
export function trackReferralDetailView(
  metadata: BaseEventMetadata & { referralId?: string; referralStatus?: string; riskScore?: number } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      referralId: metadata.referralId,
      referralStatus: metadata.referralStatus,
      riskScore: metadata.riskScore,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 👁️ ${PartnerReferralEventType.REFERRALS_DETAIL_VIEW}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_DETAIL_VIEW, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referral detail view:', error);
  }
}

/**
 * Track referral detail load failure
 */
export function trackReferralDetailFailed(
  metadata: BaseEventMetadata & { referralId?: string } = {},
  error: Error | string,
) {
  try {
    const sanitized = sanitizeTrackingData({
      errorMessage: typeof error === 'string' ? error : error.message,
      practiceId: metadata.practiceId,
      referralId: metadata.referralId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ❌ ${PartnerReferralEventType.REFERRALS_DETAIL_FAILED}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_DETAIL_FAILED, sanitized);
    if (error instanceof Error) {
      recordRumError(error, { context: 'referral_detail_fetch' });
    }
  } catch (err) {
    console.error('[RUM] Failed to track referral detail failed:', err);
  }
}

/**
 * Track referrals search
 */
export function trackReferralsSearch(
  metadata: BaseEventMetadata & { searchLength?: number; resultCount?: number } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      resultCount: metadata.resultCount,
      searchTermLength: metadata.searchLength,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔍 ${PartnerReferralEventType.REFERRALS_SEARCH}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_SEARCH, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referrals search:', error);
  }
}

/**
 * Track referrals filter change
 */
export function trackReferralsFilterChange(
  metadata: BaseEventMetadata & { filterType?: string; filterValue?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      filterType: metadata.filterType,
      filterValue: metadata.filterValue,
      practiceId: metadata.practiceId,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] 🔽 ${PartnerReferralEventType.REFERRALS_FILTER_CHANGE}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_FILTER_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referrals filter change:', error);
  }
}

/**
 * Track referrals sort change
 */
export function trackReferralsSortChange(
  metadata: BaseEventMetadata & { sortColumn?: string; sortDirection?: string } = {},
) {
  try {
    const sanitized = sanitizeTrackingData({
      practiceId: metadata.practiceId,
      sortColumn: metadata.sortColumn,
      sortDirection: metadata.sortDirection,
      tenantId: metadata.tenantId,
      timestamp: new Date().toISOString(),
    });
    console.log(`[RUM] ↕️ ${PartnerReferralEventType.REFERRALS_SORT_CHANGE}`, sanitized);
    recordRumEvent(PartnerReferralEventType.REFERRALS_SORT_CHANGE, sanitized);
  } catch (error) {
    console.error('[RUM] Failed to track referrals sort change:', error);
  }
}
